import { z } from 'zod'

import {
  bytesSchema,
  capabilitySchema,
  currencyGroupingSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  limitSchema,
  planCodeSchema,
  planFeatureSchema,
  roleSchema,
  slugSchema,
  tenantStatusSchema,
  usernameSchema,
} from '../common'

/**
 * A8 · `GET /me` — the capability token (plan §5.4).
 *
 * Called on launch and again after every unlock. It is the one payload that
 * tells a frontend what it may draw: the tab bar, the Reports tab, the money
 * rows, the "Add person" button, the upgrade prompt.
 *
 * **The UI reads this to decide what to show; the server still enforces every
 * rule independently.** A capability absent from this list is a reason to hide
 * a button — it is never a reason for a handler to skip its own guard, and a
 * capability present in it never grants anything on its own. Anyone editing an
 * endpoint should assume this payload is attacker-controlled.
 */
export const actorSchema = z.object({
  id: idSchema,
  name: z.string(),
  username: usernameSchema,
  role: roleSchema,
  mustChangePassword: z.boolean(),
})

export const tenantSummarySchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: slugSchema,
  status: tenantStatusSchema,
  currencyGrouping: currencyGroupingSchema,
  /** Set while on trial, so the app can count down without a second call. */
  trialEndsAt: isoInstantSchema.nullish(),
})

export const planSummarySchema = z.object({
  code: planCodeSchema,
  name: z.string(),
  features: z.array(planFeatureSchema),
})

/**
 * Price Lock (D4). `enabled` is the tenant setting, `unlocked` is this
 * session's state right now, `unlockedUntil` is when it lapses. The client
 * re-locks its own UI at that instant; the server re-locks regardless.
 */
export const priceLockStateSchema = z.object({
  enabled: z.boolean(),
  unlocked: z.boolean(),
  autoLockSeconds: z.number().int().positive(),
  unlockedUntil: isoInstantSchema.nullish(),
  /** False until the owner has set a PIN (A11); the sheet then asks to set one. */
  pinSet: z.boolean(),
  biometricAllowed: z.boolean(),
})

/** `max: null` is unlimited (§4.17). "Near a limit" is ≥ 80% of a quota. */
export const countLimitSchema = z.object({
  current: z.number().int().nonnegative(),
  max: limitSchema,
})

export const storageLimitSchema = z.object({
  currentBytes: bytesSchema,
  maxBytes: limitSchema,
})

export const limitsSchema = z.object({
  animals: countLimitSchema,
  users: countLimitSchema,
  storage: storageLimitSchema,
})

export const meResponse = z.object({
  user: actorSchema,
  tenant: tenantSummarySchema,
  plan: planSummarySchema,
  capabilities: z.array(capabilitySchema),
  priceLock: priceLockStateSchema,
  limits: limitsSchema,
  /**
   * The server's calendar date in `Asia/Karachi` (§4.21). The server is the
   * only authority for "today": a phone with a wrong clock, or one crossing
   * midnight UTC at 5am PKT, must not decide how many days an animal was held.
   * Every screen that prints a date derives it from here, not from `new Date`.
   */
  today: isoDateSchema,
  /**
   * Set only when a platform actor is inside this book through P5, so the
   * trader-facing UI can show the "support is viewing your book" banner and
   * the console can show whose session it is standing in.
   */
  platformAccess: z
    .object({
      accessId: idSchema,
      actorName: z.string(),
      mode: z.enum(['READ_ONLY', 'EDIT']),
      expiresAt: isoInstantSchema,
    })
    .nullish(),
})

export type MeResponse = z.infer<typeof meResponse>
export type Actor = z.infer<typeof actorSchema>
export type TenantSummary = z.infer<typeof tenantSummarySchema>
export type PriceLockState = z.infer<typeof priceLockStateSchema>
export type Limits = z.infer<typeof limitsSchema>
